import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import { TooltipButton } from "@/components/common/TooltipButton";

/** Gap kept between the panel's edges and the viewport. */
const VIEWPORT_MARGIN = 16;

/** Gap between the anchor and a `placement="below"` panel. */
const BELOW_GAP = 4;

/** Gap between the row and a `placement="side"` panel. */
const SIDE_GAP = 12;

/// Width band for a `placement="side"` panel. It takes whatever room is left to
/// the right of the row, but never reads as a sliver: below the minimum it slides
/// back over the row's right edge instead of thinning further.
const SIDE_MIN_WIDTH = 320;
const SIDE_MAX_WIDTH = 480;

/// Unmount delay, read off `--duration-short` so it can't drift from the
/// `.row-overlay` transition that plays the panel out.
const exitDelayMs = (): number => {
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--duration-short")
    .trim();
  const value = parseFloat(raw);
  if (Number.isNaN(value)) return 0;
  return raw.endsWith("ms") ? value : value * 1000;
};

interface Box {
  top: number;
  left: number;
  width: number;
  contentMaxHeight: number;
}

interface RowOverlayProps {
  open: boolean;
  onClose: () => void;
  /// The row the panel opens from — supplies the top edge, and the horizontal
  /// extents unless `spanFrom` overrides them. Keep it in the layout (hidden,
  /// not unmounted) so the list doesn't reflow while the panel is open.
  anchor: HTMLElement | null;
  /// Element whose left/width the panel adopts. Pass the grid when the anchor
  /// is one narrow cell of it. Unlike a bare anchor, the panel's *border box*
  /// lands on it — the surface reads flush with the span's first and last cell.
  spanFrom?: HTMLElement | null;
  /// `"cover"` lands the panel on the anchor, so the row appears to lift off the
  /// list — pass the row's own markup as `header` to complete the illusion.
  /// `"below"` drops it under the anchor instead, for anchors that must stay
  /// visible (a selected card whose selection is the context).
  /// `"side"` puts it in the room to the right of the row, centred on it, so the
  /// list stays readable — see `useRowOverlayPlacement`.
  placement?: "cover" | "below" | "side";
  /// The anchor's own markup, restated so the panel reads as the row it covers.
  /// Omit under `"below"`, where the anchor itself is still on screen.
  header?: React.ReactNode;
  ariaLabel: string;
  children: React.ReactNode;
}

/// A list row that opens into a floating panel instead of an inline section: the
/// panel grows downward from the row and dismisses on ✕, Esc, or a click
/// outside.
export const RowOverlay: React.FC<RowOverlayProps> = ({
  open,
  onClose,
  anchor,
  spanFrom,
  placement = "cover",
  header,
  ariaLabel,
  children,
}) => {
  const [mounted, setMounted] = useState(open);
  const [entered, setEntered] = useState(false);
  const [box, setBox] = useState<Box | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  // Mounting must happen in the render that hides the row, not in an effect
  // afterwards: an effect lands after paint, so the row would blink out for a
  // frame with nothing covering it. This is React's derived-state-from-props
  // escape hatch — it re-renders immediately, before the browser paints.
  if (open && !mounted) setMounted(true);

  // Grown height comes from `measureRef`, which is never capped — reading the
  // scrolling wrapper instead would feed its own cap back into the clamp. Under
  // `"cover"` the card's border + padding is backed out of the top, so its
  // *content* box lands on the row rather than its border box; `"below"` starts
  // at the row's bottom edge, where there's nothing to align to.
  // `collapsed` measures the panel as just its header, which is what a closing
  // panel shrinks to — an upward-clamped one then lands back on its row instead
  // of unmounting a row-height above it. The content cap is left alone there:
  // re-clamping it would reflow the body mid-collapse.
  const place = useCallback(
    (collapsed = false) => {
      if (!anchor?.isConnected) return;
      const card = cardRef.current;
      if (!card) return;
      const row = anchor.getBoundingClientRect();
      const span = (spanFrom ?? anchor).getBoundingClientRect();
      const s = getComputedStyle(card);
      const px = (value: string) => parseFloat(value) || 0;
      const insetTop = px(s.borderTopWidth) + px(s.paddingTop);
      const insetLeft = px(s.borderLeftWidth) + px(s.paddingLeft);
      const insetRight = px(s.borderRightWidth) + px(s.paddingRight);
      const chrome =
        insetTop + px(s.paddingBottom) + px(s.borderBottomWidth) || 0;
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      const body = collapsed ? 0 : (measureRef.current?.offsetHeight ?? 0);
      const grown = chrome + headerHeight + body;
      const side = placement === "side";
      const preferredTop = side
        ? row.top + row.height / 2 - grown / 2
        : placement === "below"
          ? row.bottom + BELOW_GAP
          : row.top - insetTop;
      const highest = window.innerHeight - grown - VIEWPORT_MARGIN;
      const top = Math.round(
        Math.max(VIEWPORT_MARGIN, Math.min(preferredTop, highest)),
      );
      // Only a bare anchor gets the padding backed out (its content box has to
      // land on the row it covers); a `spanFrom` span is a set of cells the
      // surface must sit flush with, so it takes the span's edges as they are.
      const flush = spanFrom != null;
      // A side panel takes the room right of the row, not the row's span.
      const width = side
        ? Math.round(
            Math.min(
              SIDE_MAX_WIDTH,
              Math.max(
                SIDE_MIN_WIDTH,
                window.innerWidth - VIEWPORT_MARGIN - (row.right + SIDE_GAP),
              ),
            ),
          )
        : Math.round(flush ? span.width : span.width + insetLeft + insetRight);
      const left = side
        ? Math.round(
            Math.min(
              row.right + SIDE_GAP,
              window.innerWidth - VIEWPORT_MARGIN - width,
            ),
          )
        : Math.round(flush ? span.left : span.left - insetLeft);
      // A side panel is re-centred as it grows, so it gets the whole viewport
      // height to work with; a cover/below one grows downward from where it
      // landed and only gets what's left below its top.
      const availableHeight = side
        ? window.innerHeight - 2 * VIEWPORT_MARGIN
        : window.innerHeight - top - VIEWPORT_MARGIN;
      setBox((prev) => ({
        top,
        left,
        width,
        contentMaxHeight:
          collapsed && prev
            ? prev.contentMaxHeight
            : availableHeight - headerHeight - chrome,
      }));
    },
    [anchor, spanFrom, placement],
  );

  // A side panel wipes sideways at a fixed height, so it has nothing to collapse
  // to; the growing placements shrink back onto their anchor on the way out.
  const collapsesOnExit = placement !== "side";
  const reposition = useCallback(
    () => place(collapsesOnExit && !open),
    [place, collapsesOnExit, open],
  );

  useLayoutEffect(() => {
    if (mounted) place();
  }, [mounted, place]);

  // Play the panel out, then unmount once the transitions have run.
  useEffect(() => {
    if (open || !mounted) return;
    setEntered(false);
    if (collapsesOnExit) place(true);
    const id = window.setTimeout(() => setMounted(false), exitDelayMs());
    return () => window.clearTimeout(id);
  }, [open, mounted, place, collapsesOnExit]);

  // Enter on the frame after the closed state paints, so the reveal animates.
  useEffect(() => {
    if (!mounted || !open) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [mounted, open]);

  // Bodies that lazy-load their data change height after the fetch lands.
  useEffect(() => {
    if (!mounted) return;
    const content = measureRef.current;
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(reposition)
        : null;
    if (content) ro?.observe(content);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [mounted, reposition]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !e.defaultPrevented) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // There's no scrim to catch the dismiss click, which is the point: a press
  // lands on whatever is under it, so clicking another row switches the panel
  // in one go. Presses on the panel's own row are ignored (it would close and
  // reopen), as are portaled menus and dialogs the panel owns.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (cardRef.current?.contains(target) || anchor?.contains(target)) return;
      if (
        target.closest(
          '[role="dialog"],[role="menu"],[role="tooltip"],[role="listbox"],.sidebar-dropdown',
        )
      ) {
        return;
      }
      onClose();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, onClose, anchor]);

  // Take focus so Esc and Tab land in the panel; hand it back on close — unless
  // focus has already moved on (clicking straight into another row's panel).
  useEffect(() => {
    if (!mounted) return;
    const card = cardRef.current;
    const previous = document.activeElement;
    card?.focus({ preventScroll: true });
    return () => {
      const active = document.activeElement;
      const ours =
        active === null || active === document.body || !!card?.contains(active);
      if (ours && previous instanceof HTMLElement && previous.isConnected) {
        previous.focus({ preventScroll: true });
      }
    };
  }, [mounted]);

  if (!mounted) return null;

  const side = placement === "side";
  // Closing fades the whole panel out, so the ✕ and the actions the header
  // restates leave with it instead of standing there crisp until unmount. Only
  // `"side"` fades *in* the same way: the other placements cover a row that's
  // already hidden, where a transparent frame reads as the row blinking out.
  const faded = side ? !entered : !open;

  return createPortal(
    <>
      <div
        ref={cardRef}
        role="dialog"
        aria-label={ariaLabel}
        data-open={entered}
        data-faded={faded}
        data-mode={placement}
        tabIndex={-1}
        className="row-overlay fixed z-50 flex flex-col p-2 outline-none"
        style={{
          top: box?.top,
          left: box?.left,
          width: box?.width,
        }}
      >
        {/* The surface is its own layer so it can fade in under content that must
            not: a covering header would read as the row blinking out. */}
        <div
          aria-hidden
          className="row-overlay-surface absolute inset-0 -z-10 bg-bg-subtle border border-border rounded-app shadow-lg"
        />
        {/* With no header the ✕ still gets its own row — floating it over the
            body would collide with the first line of content. */}
        <div ref={headerRef} className="shrink-0 flex items-center gap-1">
          {header && <div className="min-w-0 flex-1">{header}</div>}
          {/* `flex` and not an inline wrapper: an inline box puts the button on
              a text baseline, which sits it ~2px above the row's own icons. */}
          <span
            className={`row-overlay-chrome shrink-0 pr-1 flex items-center ${header ? "" : "ml-auto"}`}
          >
            <TooltipButton
              title="Close"
              aria-label="Close"
              variant="default"
              size="xs"
              onClick={onClose}
            >
              <CloseIcon />
            </TooltipButton>
          </span>
        </div>
        <div className="row-overlay-body min-h-0">
          <div className="min-h-0 overflow-hidden">
            <div
              className="overflow-y-auto"
              style={{ maxHeight: box?.contentMaxHeight }}
            >
              <div ref={measureRef}>{children}</div>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};
